import { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { MaskLines, Reveal } from "../components/Reveal";
import { BRAND, buildWhatsAppLink } from "../data/brand";
import { useSEO } from "../lib/useSEO";

export default function Contact() {
  useSEO(
    "Contact SK Fragrances | Incense Manufacturer Bangalore",
    "Get in touch with SK Fragrances for retail enquiries, bulk requirements and distribution opportunities. Incense stick manufacturer in Bangalore since 1980."
  );
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", requirement: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      `Hi ${BRAND.name}, I would like to make an enquiry.`,
      "",
      `Name: ${form.name || "-"}`,
      `Phone: ${form.phone || "-"}`,
      `Email: ${form.email || "-"}`,
      `Company / Business: ${form.company || "-"}`,
      `Requirement: ${form.requirement || "-"}`,
      form.message ? `Message: ${form.message}` : "",
    ].filter(Boolean);
    window.open(buildWhatsAppLink(lines.join("\n")), "_blank");
  };

  const field = "w-full bg-transparent border-b border-charcoal/25 focus:border-gold outline-none py-3 text-charcoal placeholder:text-charcoal/40 transition-colors duration-300";

  const contacts = [
    { icon: MapPin, label: "Location", value: `${BRAND.city}, ${BRAND.region}`, href: null },
    { icon: Phone, label: "Phone", value: BRAND.phone, href: `tel:${BRAND.phone}` },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: buildWhatsAppLink(`Hi ${BRAND.name}, I would like to enquire about your incense products.`) },
    { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}` },
  ];

  return (
    <div data-testid="contact-page" className="bg-ivory">
      <section className="pt-40 md:pt-52 pb-16 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="text-sm tracking-[0.3em] text-gold mb-6">GET IN TOUCH</p>
          <MaskLines lines={["Let's Talk", "Fragrance."]} className="font-serif text-6xl md:text-8xl leading-[0.95] text-charcoal" />
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-xl text-charcoal/65 leading-relaxed">
              Looking for our products, bulk requirements, retail enquiries or distribution
              opportunities? We'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-16">
          {/* details */}
          <div>
            <h2 className="font-serif text-4xl text-charcoal mb-2">SK Fragrances</h2>
            <p className="text-charcoal/60 mb-10">Crafting authentic incense in Bangalore since 1980.</p>
            <div className="space-y-px bg-charcoal/10 border border-charcoal/10">
              {contacts.map((c) => {
                const Inner = (
                  <div className="bg-ivory p-6 flex items-center gap-5 group hover:bg-white transition-colors duration-400">
                    <span className="w-12 h-12 border border-gold/50 flex items-center justify-center text-gold shrink-0">
                      <c.icon size={20} />
                    </span>
                    <div>
                      <p className="text-[10px] tracking-[0.25em] text-charcoal/50">{c.label.toUpperCase()}</p>
                      <p className="text-lg text-charcoal">{c.value}</p>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={`contact-${c.label.toLowerCase()}`}>{Inner}</a>
                ) : (
                  <div key={c.label}>{Inner}</div>
                );
              })}
            </div>
          </div>

          {/* form */}
          <div>
            <p className="text-sm tracking-[0.3em] text-gold mb-6">ENQUIRY FORM</p>
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <input required data-testid="contact-name" className={field} placeholder="Name" value={form.name} onChange={set("name")} />
                <input required data-testid="contact-phone" className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <input type="email" data-testid="contact-email" className={field} placeholder="Email" value={form.email} onChange={set("email")} />
                <input data-testid="contact-company" className={field} placeholder="Company / Business" value={form.company} onChange={set("company")} />
              </div>
              <input data-testid="contact-requirement" className={field} placeholder="Requirement (e.g. bulk, retail, distribution)" value={form.requirement} onChange={set("requirement")} />
              <textarea rows={4} data-testid="contact-message" className={`${field} resize-none`} placeholder="Message" value={form.message} onChange={set("message")} />
              <button type="submit" data-testid="contact-submit" className="btn-line w-full bg-charcoal text-ivory py-4 text-sm tracking-[0.2em] hover:text-charcoal">
                SUBMIT ENQUIRY →
              </button>
              <p className="text-[11px] text-center text-charcoal/45">Opens WhatsApp with your enquiry pre-filled.</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
